import { Activity, WellnessDimension } from '../types/recommendations';

/**
 * Shared mapper for GoIcon "activity-like" payloads.
 * Used by event recommendations and facility activities list.
 */

export interface GoIconActivityMappingOptions {
  // Optional mapping from GoIcon dimension id -> our wellness dimension bucket.
  // This should be derived from facility metric config mappings.
  goiconDimensionIdToWellness?: Record<number, WellnessDimension>;
}

export function mapActivitiesResponse(data: any, opts?: GoIconActivityMappingOptions): Activity[] {
  const events = data?.events || data?.recommendations || data?.activities || data?.items || data || [];
  if (!Array.isArray(events)) return [];
  return events
    .filter((item: any) => item?.id != null || item?.uuid != null)
    .map((item: any) => mapToActivity(item, opts));
}

export function mapToActivity(item: any, opts?: GoIconActivityMappingOptions): Activity {
  const mix = normalizeMix(item, opts);

  // GoIcon source fields for UI enhancements (registration link + dimension labels)
  const goiconStartedDateRaw = item?.i_started_date ?? item?.i_occurrence_id;
  const goiconStartedDate = Number.isFinite(Number(goiconStartedDateRaw)) ? Number(goiconStartedDateRaw) : undefined;

  const maxDim = item?.maxDimension;
  const maxDimIdRaw = maxDim?.fk_dimension_id ?? maxDim?.dimension?.id;
  const maxDimId = Number.isFinite(Number(maxDimIdRaw)) ? Number(maxDimIdRaw) : undefined;
  const maxDimNameRaw = maxDim?.dimension?.v_name ?? maxDim?.dimension?.name ?? maxDim?.v_name ?? maxDim?.name;
  const maxDimName = typeof maxDimNameRaw === 'string' ? maxDimNameRaw.trim() : undefined;

  // If maxDimension isn't present, derive from eventDimensions by highest i_percentage
  let derivedMaxId = maxDimId;
  let derivedMaxName = maxDimName;
  if ((!derivedMaxId || !derivedMaxName) && Array.isArray(item?.eventDimensions) && item.eventDimensions.length > 0) {
    let bestPct = -Infinity;
    for (const ed of item.eventDimensions) {
      const pct = Number(ed?.i_percentage ?? 0);
      if (!Number.isFinite(pct) || pct <= bestPct) continue;
      const dimObj = ed?.dimension || {};
      const did = Number(ed?.fk_dimension_id ?? dimObj?.id);
      const nameRaw = dimObj?.v_name ?? dimObj?.name;
      const nm = typeof nameRaw === 'string' ? nameRaw.trim() : '';
      if (!Number.isFinite(did) || !nm) continue;
      bestPct = pct;
      derivedMaxId = did;
      derivedMaxName = nm;
    }
  }
  return {
    id: String(item?.id ?? item?.uuid),
    title: item?.v_title || item?.title || item?.name || undefined,
    description: item?.t_description || item?.description || '',
    startTime: item?.start_time || item?.startTime || item?.start_date?.m || (item?.i_started_date ? new Date(item.i_started_date * 1000).toISOString() : undefined),
    endTime: item?.end_time || item?.endTime || item?.end_date?.m || (item?.i_ended_date ? new Date(item.i_ended_date * 1000).toISOString() : undefined),
    // Some GoIcon endpoints return an object for location/venue (e.g. { id, v_name, v_abbreviation }).
    // Normalize to a safe string so React doesn't try to render an object.
    location: normalizeLocation(item?.location ?? item?.venue ?? item?.room ?? item?.place),
    tags: item?.tags || [],
    mix,
    goiconStartedDate,
    goiconMaxDimensionId: derivedMaxId,
    goiconMaxDimensionName: derivedMaxName,
  };
}

function normalizeLocation(raw: unknown): string | undefined {
  if (!raw) return undefined;
  if (typeof raw === 'string') {
    const s = raw.trim();
    return s.length > 0 ? s : undefined;
  }
  if (typeof raw === 'number' || typeof raw === 'boolean') return String(raw);

  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const name = (obj.v_name ?? obj.name ?? obj.title ?? obj.label ?? obj.location) as unknown;
    const abbr = (obj.v_abbreviation ?? obj.abbreviation ?? obj.short ?? obj.code) as unknown;

    const nameStr = typeof name === 'string' ? name.trim() : (typeof name === 'number' ? String(name) : '');
    const abbrStr = typeof abbr === 'string' ? abbr.trim() : (typeof abbr === 'number' ? String(abbr) : '');

    if (nameStr && abbrStr) return `${nameStr} (${abbrStr})`;
    if (nameStr) return nameStr;
    if (abbrStr) return abbrStr;

    const id = obj.id;
    if (typeof id === 'string' && id.trim()) return id.trim();
    if (typeof id === 'number') return String(id);
  }

  // Last resort: stringify (avoid "[object Object]" if possible)
  try {
    const s = JSON.stringify(raw);
    return s && s !== '{}' ? s : undefined;
  } catch {
    return undefined;
  }
}

function normalizeMix(item: any, opts?: GoIconActivityMappingOptions) {
  const dims: WellnessDimension[] = ['mind','body','social','spirit','environment','purpose','financial','other'];
  const mix: Record<string, number> = {};
  let sum = 0;
  for (const d of dims) {
    const v = Number(item?.mix?.[d] ?? item?.dimension_mix?.[d] ?? 0);
    mix[d] = isFinite(v) && v > 0 ? v : 0;
    sum += mix[d];
  }

  // Fallback: derive mix from eventDimensions[] if mix wasn't provided
  if (sum === 0 && Array.isArray(item?.eventDimensions)) {
    for (const ed of item.eventDimensions) {
      const dimObj = ed?.dimension || {};
      const dimId = Number(ed?.fk_dimension_id ?? dimObj?.id);
      const nameRaw = dimObj.v_name ?? dimObj.name;
      const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
      const pct = Number(ed?.i_percentage ?? 0);
      if (!name || !isFinite(pct) || pct <= 0) continue;

      const mapped = Number.isFinite(dimId) ? opts?.goiconDimensionIdToWellness?.[dimId] : undefined;
      const key = mapped || mapGoIconDimensionNameToWellnessDimension(name);
      mix[key] = (mix[key] || 0) + pct;
      sum += pct;
    }
  }

  if (sum > 0) {
    for (const d of dims) mix[d] = mix[d] / sum; // normalize to ~1
  }
  return mix;
}

function mapGoIconDimensionNameToWellnessDimension(name: string): WellnessDimension {
  const n = name.toLowerCase();

  if (n.includes('physical') || n.includes('nutri')) return 'body';
  if (n.includes('social')) return 'social';
  if (n.includes('spirit')) return 'spirit';
  if (n.includes('environment')) return 'environment';
  if (n.includes('purpose') || n.includes('vocational')) return 'purpose';
  if (n.includes('financial')) return 'financial';
  if (n.includes('emotional') || n.includes('intellectual') || n.includes('cognitive') || n.includes('mind')) return 'mind';

  // Health services and anything unknown goes to "other"
  return 'other';
}

